import React from 'react'
import { Button, Card, Flex, H2, Line, P, Span } from '../../lib/components'
import { OpportunityInterestDetail, OpportunityInterestListItem } from '../../lib/types'
import { displayDollar, formatCents, getTimestampWithTimezone } from '../../lib/utils/helpers'
import './allocation-requests.scss'

export type AllocationRequestDetailPresenterProps = {
  interest: OpportunityInterestDetail | null
  enrichment?: OpportunityInterestListItem
  isLoading: boolean
  onBack: () => void
}

const formatDate = (value?: string) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return getTimestampWithTimezone(date, 'DD MMM YYYY, HH:mm z')
}

const clientName = (row?: OpportunityInterestListItem) => {
  if (!row) return null
  const name = `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim()
  return name || null
}

const DetailRow = ({ label, value, testId }: { label: string; value: string; testId?: string }) => (
  <Flex variant='space-between' classes={['mt-4']} dataTestId={testId}>
    <Span variant='bold' text={label} />
    <Span variant='thin' text={value} />
  </Flex>
)

export function AllocationRequestDetailPresenter({
  interest,
  enrichment,
  isLoading,
  onBack,
}: AllocationRequestDetailPresenterProps) {
  const amountDisplay = interest
    ? enrichment?.currency_code
      ? displayDollar(String(interest.amount), enrichment.currency_code, 2, true)
      : `${formatCents(interest.amount)} (major units; currency unknown)`
    : '—'

  return (
    <Flex variant='column-center'>
      <div className='allocation-requests allocation-requests--detail w-100'>
        <div className='allocation-requests__detail-header'>
          <H2 text='Allocation Request' />
          <Button
            text='Back to list'
            variant='secondary-btn'
            handleClick={onBack}
            classes={['allocation-requests__back-btn']}
            dataTestId='allocation-request-back'
          />
        </div>
        <Line />
        {isLoading ? (
          <p
            className='allocation-requests__loading'
            data-testid='allocation-request-detail-loading'
          >
            Loading…
          </p>
        ) : !interest ? (
          <Span
            classes={['center', 'mt-24']}
            variant='thin'
            text='Allocation request not found'
            dataTestId='allocation-request-detail-empty'
          />
        ) : (
          <Card classes={['p-40', 'mt-24']} dataTestId='allocation-request-detail-card'>
            <P text='Request details' />
            <DetailRow
              label='Request ID'
              value={String(interest.id)}
              testId='allocation-request-detail-id'
            />
            <DetailRow
              label='Investor ID'
              value={String(interest.investor_id)}
              testId='allocation-request-detail-investor-id'
            />
            <DetailRow
              label='Opportunity ID'
              value={String(interest.opportunity_id)}
              testId='allocation-request-detail-opportunity-id'
            />
            <DetailRow
              label='Amount'
              value={amountDisplay}
              testId='allocation-request-detail-amount'
            />
            <DetailRow
              label='Created'
              value={formatDate(interest.created_at)}
              testId='allocation-request-detail-created'
            />
            <DetailRow
              label='Updated'
              value={formatDate(interest.updated_at)}
              testId='allocation-request-detail-updated'
            />

            {enrichment && (
              <>
                <Line />
                <P text='From list context' />
                {clientName(enrichment) && (
                  <DetailRow label='Client' value={clientName(enrichment) as string} />
                )}
                {enrichment.investor_email && (
                  <DetailRow label='Client Email' value={enrichment.investor_email} />
                )}
                {enrichment.investor_reference && (
                  <DetailRow label='Client ID' value={enrichment.investor_reference} />
                )}
                {enrichment.opportunity_title && (
                  <DetailRow label='Opportunity' value={enrichment.opportunity_title} />
                )}
                {enrichment.currency_code && (
                  <DetailRow label='Currency' value={enrichment.currency_code} />
                )}
              </>
            )}
          </Card>
        )}
      </div>
    </Flex>
  )
}
